import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import {
  RMSTextField,
  RMSSelect,
  RMSDateTimePicker,
} from '../../../shared/components/form-controls';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import SearchPlaceAutoComplete from '../../../shared/components/modules/SearchPlaceAutoComplete';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { DEFAULT_VALUES, DICT_SELECT_PROPS } from '../../constants/personDnaSampleFormConstants';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { createDnaSample, editDnaSample } from '../../services/userService';
import AddPersonDialog from './AddPersonDialog';
import AddPlaceDialog from './AddPlaceDialog';

const PersonDnaSampleDialog = ({ open, onClose, personId, sample = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [addPersonDialogOpen, setAddPersonDialogOpen] = useState(false);
  const [addPlaceDialogOpen, setAddPlaceDialogOpen] = useState(false);
  const isEditMode = Boolean(sample?.ptsDNASampleID);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(sample || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    dnaSampleTypeOptions,
    dnaSampleMethodOptions,
    dnaSampleReasonOptions,
    dnaCollectionStatusOptions,
  } = useUserDictionaryOptions();
  const ptsTakenByPersonID = useWatch({ control: methods.control, name: 'ptsTakenByPersonID' });
  const ptsCrimeLabPlaceID = useWatch({ control: methods.control, name: 'ptsCrimeLabPlaceID' });

  const handleTakenByPersonCreated = useCallback(
    (person) => {
      methods.setValue('ptsTakenByPersonID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPersonDialogOpen(false);
    },
    [methods]
  );

  const handleCrimeLabPlaceCreated = useCallback(
    (ptsPlaceID) => {
      methods.setValue('ptsCrimeLabPlaceID', ptsPlaceID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddPlaceDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        DNASampleID: data.DNASampleID || null,
        Taken: data.Taken || null,
        ptsTakenByPersonID: data.ptsTakenByPersonID || null,
        Type: data.Type || null,
        Method: data.Method || null,
        Reason: data.Reason || null,
        Status: data.Status || null,
        ptsCrimeLabPlaceID: data.ptsCrimeLabPlaceID || null,
        CrimeLabSentDateTime: data.CrimeLabSentDateTime || null,
        Notes: data.Notes || null,
      };

      if (isEditMode) {
        payload.UpdatedBy = currentUser?.Username;
      } else {
        payload.ptsPersonID = personId;
        payload.CreatedBy = currentUser?.Username;
        payload.UpdatedBy = currentUser?.Username;
      }

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () =>
          isEditMode ? editDnaSample(sample.ptsDNASampleID, payload) : createDnaSample(payload),
        onSuccess: (savedSample) => {
          dispatch(
            notify(
              isEditMode ? 'DNA Sample updated successfully' : 'DNA Sample created successfully',
              'success'
            )
          );
          onSaved?.(savedSample);
        },
      });
    },
    [isEditMode, sample, personId, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this DNA sample. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <RMSCommonDialog
      open={open}
      title={isEditMode ? 'Edit DNA Sample' : 'Add DNA Sample'}
      onClose={requestClose}
      onSave={handleSave}
      loading={submitting}
      saveDisabled={submitting}
      maxWidth="sm"
      fullWidth
      closeOnBackdropClick={false}
      saveButton={{
        show: true,
        type: 'simple',
        label: 'Save',
        variant: 'contained',
        color: 'primary',
      }}
      cancelButton={{
        show: true,
        type: 'simple',
        label: 'Close',
        variant: 'outlined',
        color: 'secondary',
      }}
      leftActions={
        isEditMode && (
          <RMSRecordHistoryButton
            disabled={!sample?.Created}
            created={sample?.Created}
            createdBy={sample?.CreatedBy}
            updated={sample?.Updated}
            updatedBy={sample?.UpdatedBy}
          />
        )
      }>
      <FormProvider {...methods}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <RMSTextField name="DNASampleID" label="DNA Sample ID" inputProps={{ maxLength: 25 }} />
          </Grid>
          <Grid item xs={6}>
            <SearchPersonAutoComplete
              name="ptsTakenByPersonID"
              label="Taken By"
              defaultPersonId={ptsTakenByPersonID}
              onAddIconClick={() => setAddPersonDialogOpen(true)}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker name="Taken" label="Taken Date" />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Type"
              label="Sample Type"
              options={dnaSampleTypeOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Method"
              label="Sample Method"
              options={dnaSampleMethodOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Reason"
              label="Sample Reason"
              options={dnaSampleReasonOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSSelect
              name="Status"
              label="Sample Status"
              options={dnaCollectionStatusOptions}
              {...DICT_SELECT_PROPS}
            />
          </Grid>
          <Grid item xs={6}>
            <SearchPlaceAutoComplete
              name="ptsCrimeLabPlaceID"
              label="Crime Lab"
              defaultPlaceId={ptsCrimeLabPlaceID}
              onAddIconClick={() => setAddPlaceDialogOpen(true)}
              showDetailsIcon={false}
            />
          </Grid>
          <Grid item xs={6}>
            <RMSDateTimePicker name="CrimeLabSentDateTime" label="Crime Lab Sent Date" />
          </Grid>
          <Grid item xs={12}>
            <RMSTextField
              name="Notes"
              label="Notes"
              multiline
              rows={4}
              inputProps={{ maxLength: 8000 }}
            />
          </Grid>
        </Grid>
      </FormProvider>

      {addPersonDialogOpen && (
        <AddPersonDialog
          open={addPersonDialogOpen}
          onClose={() => setAddPersonDialogOpen(false)}
          onPersonCreated={handleTakenByPersonCreated}
        />
      )}

      {addPlaceDialogOpen && (
        <AddPlaceDialog
          open={addPlaceDialogOpen}
          onClose={() => setAddPlaceDialogOpen(false)}
          onPlaceCreated={handleCrimeLabPlaceCreated}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </RMSCommonDialog>
  );
};

export default PersonDnaSampleDialog;
